import { ConnectionNodeType, IConnectionDrawingOptions } from '..';
import {
	ChangeSet,
	ChangeSetEntityChangeType,
	ENTRY_POINT_TYPES,
	EntryPointEntityState,
	getParentCallerEntity,
	getScopeOwner,
	PASS_THROUGH_CALLABLE_TYPES,
	PassThroughCallableEntityState,
	ProjectState,
	VALUE_READING_TYPES,
	VALUE_WRITING_TYPES,
	ValueReadingEntityState,
	ValueWritingEntityState,
	VariableDeclarationState,
	VariableInstanceState,
} from '@elyx-code/project-logic-tree';
import { connectPassthroughCallable } from './utils';

export function createVariableInstanceFromDeclaration(
	from: VariableDeclarationState,
	connectionOptions: IConnectionDrawingOptions | null,
	project: ProjectState,
	changeSet: ChangeSet
): VariableInstanceState {
	// Create a new variable instance of this variable declaration at this coordinates
	const newVariableInstance = from.createInstance(project, changeSet);

	changeSet.add(newVariableInstance, ChangeSetEntityChangeType.Added);

	// Add any related entity that is inferred from the connection data
	connectPassthroughCallable(
		newVariableInstance,
		connectionOptions,
		project,
		changeSet
	);

	if (
		connectionOptions?.nodeType === ConnectionNodeType.PropertyIn &&
		!!connectionOptions.target
	) {
		const valueReader = connectionOptions.target as ValueReadingEntityState;

		if (valueReader && VALUE_READING_TYPES.includes(valueReader.type)) {
			newVariableInstance.addValueReader(valueReader, changeSet);
			valueReader.setValueWriter(newVariableInstance, changeSet);
		}
	} else if (
		connectionOptions?.nodeType === ConnectionNodeType.PropertyOut &&
		!!connectionOptions.source
	) {
		const valueWritter = connectionOptions.source as ValueWritingEntityState;

		if (valueWritter && VALUE_WRITING_TYPES.includes(valueWritter.type)) {
			newVariableInstance.setValueWriter(valueWritter, changeSet);
			valueWritter.addValueReader(newVariableInstance, changeSet);

			const writterScopeOwner = getScopeOwner(valueWritter, project);

			newVariableInstance.setParent(writterScopeOwner, changeSet);

			const writterCaller = getParentCallerEntity(from);

			if (!!writterCaller && ENTRY_POINT_TYPES.includes(writterCaller.type)) {
				(writterCaller as EntryPointEntityState).addCall(
					newVariableInstance
				);
				changeSet.add(
					writterCaller,
					ChangeSetEntityChangeType.Affected
				);
				newVariableInstance.addEntryCaller(
					writterCaller as EntryPointEntityState,
					changeSet
				);
			} else if (
				!!writterCaller &&
				PASS_THROUGH_CALLABLE_TYPES.includes(writterCaller.type)
			) {
				(writterCaller as PassThroughCallableEntityState).addSuccessCall(
					newVariableInstance,
					changeSet
				);
				newVariableInstance.addSuccessCaller(
					writterCaller as PassThroughCallableEntityState,
					changeSet
				);

				changeSet.add(
					writterCaller,
					ChangeSetEntityChangeType.Updated
				);
			}
		}
	}

	return newVariableInstance;
}
